/**
 * Campo para escribir un monto en pesos, con los miles separados por punto
 * mientras se escribe: `2.400.000` y no `2400000`. Una balsa cuesta millones, y
 * contar ceros a ojo es la forma mas facil de publicar un precio con uno de mas.
 *
 * El valor que sube al formulario son SOLO los digitos (`"2400000"`): los puntos
 * son de la pantalla, no del dato.
 *
 * **Formatea en cada tecla y no al salir del campo**, al reves que el RUT del
 * checkout. Alla reordenar los puntos mientras se escribe le movia el cursor a
 * quien corregia en el medio; aca eso se resuelve devolviendo el cursor a su
 * lugar, contando cuantos DIGITOS tenia a la izquierda —que es lo unico que no
 * cambia cuando aparece o desaparece un punto—.
 */
import { useLayoutEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';

import { numero } from '../../utilidades/formato';

/** `"2400000"` -> `"2.400.000"`. Vacio se queda vacio: no es lo mismo que cero. */
function conMiles(digitos: string): string {
  return digitos.length === 0 ? '' : numero(Number(digitos));
}

function soloDigitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

export interface EntradaMontoProps {
  id: string;
  /** Solo digitos, sin puntos. */
  valor: string;
  alCambiar(digitos: string): void;
  placeholder?: string;
  required?: boolean;
}

export function EntradaMonto({ id, valor, alCambiar, placeholder, required }: EntradaMontoProps) {
  const entrada = useRef<HTMLInputElement>(null);
  // Cuantos digitos quedaron a la izquierda del cursor en la ultima tecla.
  const digitosAntes = useRef<number | null>(null);

  const mostrado = conMiles(valor);

  // Despues de pintar el texto formateado, el cursor vuelve a quedar tras el
  // mismo digito. Sin esto salta al final en cada tecla.
  useLayoutEffect(() => {
    const campo = entrada.current;
    const objetivo = digitosAntes.current;
    if (campo === null || objetivo === null) return;
    digitosAntes.current = null;

    let posicion = 0;
    let vistos = 0;
    while (posicion < mostrado.length && vistos < objetivo) {
      if (/\d/.test(mostrado.charAt(posicion))) vistos += 1;
      posicion += 1;
    }
    campo.setSelectionRange(posicion, posicion);
  }, [mostrado]);

  function cambiar(evento: ChangeEvent<HTMLInputElement>): void {
    const campo = evento.target;
    const cursor = campo.selectionStart ?? campo.value.length;
    digitosAntes.current = soloDigitos(campo.value.slice(0, cursor)).length;

    // Sin ceros a la izquierda: "007" es 7, y formateado se veria como "7" igual.
    const digitos = soloDigitos(campo.value).replace(/^0+(?=\d)/, '');
    alCambiar(digitos);
  }

  return (
    <input
      ref={entrada}
      id={id}
      className="num"
      inputMode="numeric"
      autoComplete="off"
      value={mostrado}
      onChange={cambiar}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(required === true ? { required: true } : {})}
    />
  );
}
